import { InfoState } from '../ui';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from '../ui/notify';
import { endZoomLiveClass } from '../../services/organization/classroom/classroomLiveclasses.service';

// Zoom SDK Error Boundary
class ZoomErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error("[Meeting] View could not render:", error, info);
  }
  render() {
    if (this.state.hasError) return <InfoState title="Let’s reconnect to your meeting" message="Please reopen the meeting from your classroom." action={<a href="/" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}>Back to your workspace</a>} />;
    return this.props.children;
  }
}

const ZoomMeeting = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initializationStarted, setInitializationStarted] = useState(false);

  const meetingNumber = searchParams.get('meetingNumber');
  const role = searchParams.get('role');
  const signature = searchParams.get('signature');
  const sdkKey = searchParams.get('sdkKey');
  const userName = searchParams.get('userName') || (role === '1' ? 'Host' : 'Attendee');
  const meetingPassword = searchParams.get('password') || '';
  const liveClassId = searchParams.get('liveClassId'); // Get live class ID for API calls
  const zak = searchParams.get('zak') || '';

  useEffect(() => {
    if (!meetingNumber || !signature || !sdkKey || role === null) {
      setError('Missing required meeting parameters');
      setLoading(false);
      return;
    }

    fetch('https://source.zoom.us/ping', { mode: 'no-cors' }).catch(() => {});

    return () => {
      // Clear Zoom-rendered DOM on cleanup
      try {
        const zoomRoot = document.getElementById('zoom-root');
        if (zoomRoot) zoomRoot.innerHTML = '';
      } catch (e) {
        console.warn('Zoom cleanup error:', e);
      }
    };
  }, [meetingNumber, signature, sdkKey, role]);

  useEffect(() => {
    if (!initializationStarted && !error) {
      setInitializationStarted(true);

      const timeoutId = setTimeout(() => {
        if (loading) {
          setError('Meeting initialization timed out. Please try again.');
          setLoading(false);
        }
      }, 60000);

      initializeZoomMeeting().finally(() => clearTimeout(timeoutId));
    }
  }, [initializationStarted, loading, error]);

  const loadZoomSDK = async () => {
    if (window.ZoomMtg) return window.ZoomMtg;

    const scripts = [
      'https://source.zoom.us/4.0.0/lib/vendor/react.min.js',
      'https://source.zoom.us/4.0.0/lib/vendor/react-dom.min.js',
      'https://source.zoom.us/4.0.0/lib/vendor/redux.min.js',
      'https://source.zoom.us/4.0.0/lib/vendor/redux-thunk.min.js',
      'https://source.zoom.us/4.0.0/zoom-meeting-4.0.0.min.js'
    ];

    await Promise.all(scripts.map(src => new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"]`)) return res();
      const s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = res;
      s.onerror = () => rej(new Error(`Failed to load: ${src}`));
      document.head.appendChild(s);
    })));

    return new Promise((resolve, reject) => {
      let attempts = 0;
      (function check() {
        if (window.ZoomMtg) return resolve(window.ZoomMtg);
        if (++attempts > 20) return reject(new Error('ZoomMtg not available'));
        setTimeout(check, 100);
      })();
    });
  };

  const initializeZoomMeeting = async () => {
    try {
      const ZoomMtg = await loadZoomSDK();

      ZoomMtg.setZoomJSLib('https://source.zoom.us/4.0.0/lib', '/av');
      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();

      ZoomMtg.i18n.load('en-US');
      ZoomMtg.i18n.onLoad(() => {
        // Determine redirect URL based on user role
        const redirectUrl = role === '1'
          ? `${window.location.origin}/admin/classrooms`
          : `${window.location.origin}/user/AllClassroom`;

        ZoomMtg.init({
          leaveUrl: redirectUrl,
          disableCORP: !window.crossOriginIsolated,
          // Tell SDK which DOM node to control
          success: () => joinMeeting(ZoomMtg),
          error: (e) => {
            setError('Failed to initialize Zoom SDK: ' + (e.errorMessage || e.message));
            setLoading(false);
          }
        });
      });
    } catch (err) {
      setError('Zoom SDK init error: ' + err.message);
      setLoading(false);
    }
  };

  const joinMeeting = (ZoomMtg) => {
    let userLeftMeeting = false;

    // Listen for when user leaves meeting (this fires when "Leave Meeting" is clicked)
    ZoomMtg.inMeetingServiceListener('onUserLeave', (data) => {
      userLeftMeeting = true;
      // The leaveUrl will handle the redirect automatically
    });

    // Listen for when meeting is ended by host (this fires when "End Meeting for All" is clicked)
    ZoomMtg.inMeetingServiceListener('onMeetingEnded', (data) => {
      if (!userLeftMeeting) {
        handleMeetingEnd();
      }
    });

    // Add event listeners for meeting end events
    ZoomMtg.inMeetingServiceListener('onMeetingStatus', (data) => {
      // Only call API if user didn't just leave the meeting
      if (data.meetingStatus === 3 && !userLeftMeeting) { // 3 = Meeting ended for all
        handleMeetingEnd();
      }
    });

    ZoomMtg.join({
      meetingNumber: meetingNumber.replace(/\D/g, ''),
      signature,
      userName,
      userEmail: '',
      passWord: meetingPassword,
      zak,
      tk: '',
      success: () => {
        setLoading(false);
        toast.success('Successfully joined the meeting!');
      },
      error: (e) => {
        setError('Failed to join meeting: ' + (e.errorMessage || 'Unknown error'));
        setLoading(false);
      }
    });
  };

  const handleGoBack = () => {
    const redirectPath = role === '1' ? '/admin/classrooms' : '/user/AllClassroom';
    navigate(redirectPath);
  };

  // Function to handle meeting end via API
  const handleMeetingEnd = async () => {
    if (!liveClassId) {
      console.warn('No live class ID available for ending meeting');
      return;
    }

    try {
      await endZoomLiveClass({ live_class_id: liveClassId });
      toast.success('Meeting ended successfully!');

      // Redirect to appropriate page after successful API call
      const redirectPath = role === '1' ? '/admin/classrooms' : '/user/AllClassroom';
      setTimeout(() => {
        navigate(redirectPath);
      }, 1000); // Small delay to show the success message

    } catch (error) {
      console.error('Error ending meeting via API:', error);
      // Don't show error toast as meeting is already ended on Zoom side
      // Still redirect to appropriate page even if API call fails
      const redirectPath = role === '1' ? '/admin/classrooms' : '/user/AllClassroom';
      setTimeout(() => {
        navigate(redirectPath);
      }, 1000);
    }
  };

  if (error) {
    return (
      <div className="tw-w-full ui-container-fluid tw-h-screen tw-flex tw-items-center tw-justify-center">
        <InfoState title="Let’s reconnect to your meeting" message="We couldn’t open the meeting just now. Return to your classroom and try joining again." action={<button className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"} onClick={handleGoBack}>Back to classroom</button>} />
      </div>
    );
  }

  // React does not render anything; Zoom lives in #zoom-root
  return null;
};

export default function ZoomMeetingWithErrorBoundary() {
  return (
    <ZoomErrorBoundary>
      <ZoomMeeting />
    </ZoomErrorBoundary>
  );
}
